import { useRef, useState } from "react";
import { formatMoney } from "../api";
import { Alert, Modal, Working } from "../ui";
import { type Catalog, type LocalBuyback, recordBuyback, tillAccount } from "./engine";
import { shrink, type Taken, turned, usePastedImages } from "./photos";

/**
 * Buying at the booth (quotation C5).
 *
 * A customer offers cards. The staff photograph what is on the table, agree a price and
 * pay, and that is the whole screen: photos, price, who it came from. Nobody types card
 * names into an iPad with a queue waiting, so what the cards are is worked out later at
 * the desk, from these photographs.
 */

export function PosBuy({
  catalog,
  buyer,
  locationId,
  event,
  onClose,
  onDone,
}: {
  readonly catalog: Catalog;
  readonly buyer: { id: string; name: string };
  readonly locationId: string | null;
  readonly event: string | null;
  readonly onClose: () => void;
  readonly onDone: (message: string) => void;
}): React.ReactElement {
  const [photos, setPhotos] = useState<Taken[]>([]);
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  // Most sellers at a show give no name (D, 10 October 2026).
  const [sellerName, setSellerName] = useState("Walk-in Customer");
  const [sellerPhone, setSellerPhone] = useState("");
  const [note, setNote] = useState("");
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState<"cash" | "paynow" | "later" | undefined>();
  const camera = useRef<HTMLInputElement>(null);

  const amountCents = Math.round(Number(price.replace(/[^\d.]/g, "")) * 100);

  async function add(files: FileList | readonly File[] | null): Promise<void> {
    if (!files) return;
    setProblem(undefined);
    for (const file of [...files].slice(0, 8)) {
      try {
        const shrunk = await shrink(file);
        setPhotos((current) => [...current, shrunk]);
      } catch {
        setProblem("That photo could not be read. Take it again");
      }
    }
    if (camera.current) camera.current.value = "";
  }

  usePastedImages((files) => void add(files));

  async function save(method: "cash" | "paynow" | "later"): Promise<void> {
    if (!Number.isFinite(amountCents) || amountCents <= 0) return setProblem("Enter the purchase amount");
    if (!description.trim()) return setProblem("Enter an item description");
    if (!sellerName.trim()) return setProblem("Enter the seller name");
    if (
      photos.length === 0 &&
      !window.confirm("No photo has been added. The management dashboard will have only the item description. Continue?")
    ) {
      return;
    }

    setBusy(method);
    setProblem(undefined);
    try {
      const accountId = method === "later" ? null : tillAccount(catalog, method);
      if (method !== "later" && !accountId) {
        setProblem(
          `This iPad has no ${method === "cash" ? "cash" : "PayNow"} account configured. A system administrator sets it on the dashboard, under Devices`,
        );
        return;
      }
      const saved = await recordBuyback({
        deviceCode: catalog.device.code,
        buyer,
        locationId,
        event,
        sellerName: sellerName.trim(),
        sellerPhone: sellerPhone.trim() || null,
        description: description.trim(),
        note: note.trim() || null,
        amountCents,
        method,
        accountId,
        photos: photos.map((photo) => ({ contentType: photo.contentType, data: photo.data })),
      });
      for (const photo of photos) URL.revokeObjectURL(photo.preview);
      onDone(describe(saved));
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <Modal
      open
      size="lg"
      title="Buy from customer"
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-2 w-full">
          <button type="button" className="ti-btn ti-btn-light !mb-0" disabled={busy !== undefined} onClick={onClose}>
            Cancel
          </button>
          <div className="flex-1" />
          <button
            type="button"
            className="ti-btn ti-btn-light !mb-0"
            disabled={busy !== undefined}
            onClick={() => void save("later")}
          >
            {busy === "later" ? <Working label="Saving" /> : "Save as unpaid"}
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-secondary-full !mb-0"
            disabled={busy !== undefined}
            onClick={() => void save("paynow")}
          >
            {busy === "paynow" ? <Working label="Saving" /> : "Record PayNow payout"}
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-success-full !mb-0"
            disabled={busy !== undefined}
            onClick={() => void save("cash")}
          >
            {busy === "cash" ? <Working label="Saving" /> : "Record cash payout"}
          </button>
        </div>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <label className="form-label">Item photos</label>
          <div className="flex flex-wrap gap-3">
            {photos.map((photo) => (
              <div key={photo.id} className="relative">
                <img
                  src={photo.preview}
                  alt="What is being bought"
                  className="w-24 h-24 object-cover rounded-sm border border-defaultborder dark:border-defaultborder/10"
                />
                <button
                  type="button"
                  aria-label="Turn this photo"
                  title="Turn the photo"
                  className="absolute bottom-0 start-0 bg-dark/70 text-white text-[0.75rem] w-6 h-6 rounded-tr flex items-center justify-center"
                  onClick={(event) => {
                    event.stopPropagation();
                    void turned(photo).then((done) => setPhotos((current) => current.map((one) => (one.id === photo.id ? done : one))));
                  }}
                >
                  <i className="ri-clockwise-line" />
                </button>
                <button
                  type="button"
                  aria-label="Take this photo out"
                  className="ti-btn ti-btn-icon ti-btn-danger !absolute -top-2 -end-2 !w-6 !h-6 !mb-0"
                  onClick={() => {
                    URL.revokeObjectURL(photo.preview);
                    setPhotos((current) => current.filter((entry) => entry.id !== photo.id));
                  }}
                >
                  <i className="ri-close-line text-[0.75rem]" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="w-24 h-24 rounded-sm border-2 border-dashed border-defaultborder dark:border-white/20 flex flex-col items-center justify-center gap-1 text-[#8c9097] dark:text-white/50"
              onClick={() => camera.current?.click()}
            >
              <i className="ri-camera-line text-[1.5rem]" />
              <span className="text-[0.6875rem]">Add photo</span>
            </button>
          </div>
          <input
            ref={camera}
            id="buy-photo"
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(changed) => void add(changed.target.files)}
          />
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
            Photos upload automatically when the iPad has a connection.
          </p>
        </div>

        <div className="sm:col-span-4 col-span-12">
          <label htmlFor="buy-price" className="form-label">
            Purchase amount
          </label>
          <input
            id="buy-price"
            type="text"
            inputMode="decimal"
            className="form-control !text-[1.5rem] !h-auto !py-2 font-semibold"
            placeholder="0.00"
            value={price}
            onChange={(changed) => setPrice(changed.target.value)}
          />
        </div>
        <div className="sm:col-span-8 col-span-12">
          <label htmlFor="buy-what" className="form-label">
            Item description
          </label>
          <input
            id="buy-what"
            type="text"
            className="form-control"
            placeholder="3 sealed Scarlet & Violet boxes and 1 graded Charizard"
            value={description}
            onChange={(changed) => setDescription(changed.target.value)}
          />
        </div>

        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="buy-seller" className="form-label">
            Seller name
          </label>
          <input
            id="buy-seller"
            type="text"
            className="form-control"
            placeholder="Name"
            value={sellerName}
            onChange={(changed) => setSellerName(changed.target.value)}
          />
        </div>
        <div className="sm:col-span-6 col-span-12">
          <label htmlFor="buy-phone" className="form-label">
            Seller mobile number
          </label>
          <input
            id="buy-phone"
            type="tel"
            inputMode="tel"
            className="form-control"
            placeholder="9123 4567"
            value={sellerPhone}
            onChange={(changed) => setSellerPhone(changed.target.value)}
          />
        </div>
        <div className="col-span-12">
          <label htmlFor="buy-note" className="form-label">
            Internal notes
          </label>
          <input
            id="buy-note"
            type="text"
            className="form-control"
            placeholder="Two of the boxes are opened"
            value={note}
            onChange={(changed) => setNote(changed.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

function describe(buyback: LocalBuyback): string {
  const how =
    buyback.method === "cash"
      ? "cash payout recorded"
      : buyback.method === "paynow"
        ? "PayNow payout recorded"
        : "saved as unpaid";
  return `${buyback.reference}, ${formatMoney(buyback.amountCents)} to ${buyback.sellerName}, ${how}.`;
}
